import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LangContext";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Search, ArrowUpDown } from "lucide-react";

const FILTERS = ["all", "paid", "trial_active", "trial_expired"];
const SORTS = ["created_at", "name", "property_count", "booking_count"];

export default function AdminUsers() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const load = async () => {
    try {
      const params = { filter_by: filterBy, sort_by: sortBy, order };
      if (search) params.q = search;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBy, sortBy, order]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const togglePaid = async (id) => {
    try {
      await api.post(`/admin/users/${id}/toggle-paid`);
      toast.success(t("saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grantSubscription = async (id, plan) => {
    const raw = window.prompt(`${t("grant_subscription_confirm")}\n${t("days_label")}:`, "30");
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!Number.isFinite(days) || days < 1) return toast.error(t("fields_required"));
    try {
      await api.post(`/admin/users/${id}/grant-subscription`, { days, plan });
      toast.success(t("subscription_granted"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sortLabel = { created_at: t("sort_created"), name: t("sort_name"), property_count: t("sort_property_count"), booking_count: t("sort_booking_count") };
  const filterLabel = { all: t("filter_all"), paid: t("filter_paid"), trial_active: t("filter_trial_active"), trial_expired: t("filter_trial_expired") };

  return (
    <div className="space-y-4" data-testid="admin-users-page">
      <h1 className="text-2xl sm:text-3xl font-semibold">{t("users_list")}</h1>

      <div className="flex flex-wrap items-center gap-2 bg-white border border-[#E5E1D8] rounded-xl p-3 shadow-sm">
        <form onSubmit={onSearchSubmit} className="flex items-center gap-2 flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-1 border border-[#E5E1D8] bg-[#F9F6F0] rounded-lg px-3 py-2">
            <Search size={14} className="text-[#5C6053]" />
            <input
              data-testid="users-search-input"
              placeholder={t("search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1"
            />
          </div>
          <button data-testid="users-search-btn" type="submit" className="brand-btn px-3 py-2 rounded-lg text-sm">{t("search")}</button>
        </form>

        <div className="flex gap-1 p-1 bg-[#F9F6F0] rounded-lg border border-[#E5E1D8]">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-btn-${f}`}
              onClick={() => setFilterBy(f)}
              className={`px-3 py-1.5 text-xs rounded-md ${filterBy === f ? "bg-[#2A5948] text-white" : "text-[#1C1E17]"}`}
            >
              {filterLabel[f]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <select
            data-testid="users-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm bg-white"
          >
            {SORTS.map((s) => <option key={s} value={s}>{sortLabel[s]}</option>)}
          </select>
          <button
            data-testid="users-order-toggle"
            onClick={() => setOrder(order === "desc" ? "asc" : "desc")}
            className="p-2 border border-[#E5E1D8] rounded-lg bg-white"
            title={order}
          >
            <ArrowUpDown size={14} className={order === "asc" ? "rotate-180" : ""} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E1D8] rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3EFE7] text-[#5C6053] text-xs uppercase">
            <tr>
              <th className="text-start px-4 py-3">{t("name")}</th>
              <th className="text-start px-4 py-3">{t("phone")}</th>
              <th className="text-start px-4 py-3">{t("email")}</th>
              <th className="text-start px-4 py-3">{t("property_count")}</th>
              <th className="text-start px-4 py-3">{t("booking_count")}</th>
              <th className="text-start px-4 py-3">{t("plan_col")}</th>
              <th className="text-start px-4 py-3">{t("joined_at")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-[#5C6053]">—</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-[#E5E1D8]">
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3" dir="ltr">{u.phone}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{u.property_count}</td>
                <td className="px-4 py-3">{u.booking_count}</td>
                <td className="px-4 py-3">
                  <PlanBadge plan={u.plan} isPaid={u.is_paid} t={t} />
                </td>
                <td className="px-4 py-3 text-xs text-[#5C6053]">{u.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-end">
                  <div className="flex gap-2 justify-end flex-wrap">
                    <button data-testid={`grant-standard-${u.id}`} onClick={() => grantSubscription(u.id, "standard")} className="px-3 py-1.5 text-xs rounded-lg border border-[#2A5948] text-[#2A5948] hover:bg-[#E6F1E7]">
                      {t("grant_standard")}
                    </button>
                    <button data-testid={`grant-premium-${u.id}`} onClick={() => grantSubscription(u.id, "premium")} className="px-3 py-1.5 text-xs rounded-lg brand-btn">
                      {t("grant_premium")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlanBadge({ plan, isPaid, t }) {
  if (plan === "premium") {
    return <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#2A5948] text-white">{t("plan_premium")}</span>;
  }
  if (plan === "standard" || isPaid) {
    return <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#E6F1E7] text-[#2A5948]">{t("plan_standard")}</span>;
  }
  return <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FFF3D6] text-[#8A6D1F]">{t("plan_trial")}</span>;
}
